/**
 * Website block-DSL → Craft.js tree compiler.
 *
 * The model emits an ID-free `WebsiteDsl` (sections + content + a few style
 * knobs). This deterministic, React-free compiler assigns node IDs, wires
 * parent/child + named `linkedNodes` slots, and routes EVERY node through the
 * builder's own seed factories (`temp.ts`: tc/btn/fi/scaffold + AUTH_* props) —
 * so props always validate and round-trip in BOTH the editor and runtime
 * resolvers (the §0b master rule). The output is a normal, editable builder
 * page, not raw HTML.
 *
 * Per page it returns the UNCOMPRESSED tree object (Record<id, node>); the
 * persistence adapter compresses it via `compressToBase64` (lzutf8).
 *
 * Key correctness invariants (see migration plan §0b):
 *  - resolvedName is canonical ("TextSection"/"Button"/"FormInput") because the
 *    EDITOR resolver lacks the runtime's "Text"/"ButtonChild" aliases; the
 *    factories already emit the right resolvedName + displayName pair.
 *  - Section text is DUPLICATED: plain `text*` props (primary-lang string) AND
 *    the mirrored LangText child slots — both written in sync.
 *  - AppBar/Footer are string sentinels in the page tree; the real shared trees
 *    live on WebsiteConfig.appBar/.footer (built in persist).
 */
import {
  tc,
  btn,
  fi,
  scaffold,
  emptyFieldsGroup,
  acceptanceFi,
  AUTH_WRAPPER_PROPS,
  AUTH_CHILD_PROPS,
  ACCEPTANCE_VISIBILITY_FILTER,
  PAD,
  SEC_MARGIN,
  DIST_CARD,
  BTN_FORM_SP,
  SPEAKER_ICONS,
  pv,
  A,
  type N,
} from "@/components/builders/website/configurator-panel/components/temp";
import { normalizeToFullLocale } from "@/lib/utils/locale-normalize";
import type { LangText } from "@/lib/utils/resolve-text";
// The one place that knows the participation question's variable names. Leaf
// module (no React, no craft), so importing it here creates no cycle.
import { isAcceptanceFieldName } from "@/components/WebsiteBuilder/sections/DataInput/panel/formInputFactory";
import {
  validateWebsiteCraftStructure,
} from "./validateWebsiteCraftStructure";
import {
  MAX_PAGES,
  MAX_SECTIONS_PER_PAGE,
  MAX_REGISTER_FIELDS,
  MAX_SECTION_ITEMS,
  MAX_DATAINPUT_STEPS,
  type WebsiteDsl,
  type WebsiteDslPage,
  type WebsiteSection,
  type WebsiteButtonSpec,
  type WebsiteReveal,
  type WebsiteBackgroundToken,
  type RegisterFieldSpec,
  type WebsiteCompileError,
  type WebsiteCompileErrorCode,
} from "./websiteBlocksDsl";
import { EMAIL_OPT_IN_FIELD } from "@/lib/website/registrationConsents";
import { buildThemeConfig } from "./websiteThemeConfig";
import {
  SAMPLE_SPEAKER_IMAGES,
  SAMPLE_SPONSOR_LOGOS,
} from "@/lib/website-builder/sampleContent";

export type CompiledPage = { slug: string; pageName: string; tree: Record<string, N> };
/**
 * `warnings`: what the compile changed on its own that the model should tell
 * the organiser about (today: a required consent row lost its emailOptIn
 * binding). English on purpose — read by Velo, not shown in the UI. The key
 * is absent when there is nothing to say.
 */
export type WebsiteCompileOk = { ok: true; pages: CompiledPage[]; warnings?: string[] };

/**
 * Audit 7.12 r2 B2-7: the tool result travels back in a resultJson the
 * backend caps at 16000 chars (AiToolResultRequest) — and only AFTER the page
 * was changed, so an oversized result is a 400 on an applied edit. Hence at
 * most WARNINGS_MAX entries of at most WARNING_MAX_CHARS each; the label is
 * shortened to fit, and past the cap the last entry counts the rest.
 */
const WARNINGS_MAX = 5;
const WARNING_MAX_CHARS = 300;

function optInDroppedWarning(label: string): string {
  const head = 'Required checkbox "';
  const tail =
    '" was bound to emailOptIn; a mandatory box may not record marketing consent (GDPR Art. 7(4)), so the binding was removed. ' +
    'Ask the user before adding a separate OPTIONAL checkbox with field "emailOptIn".';
  const room = WARNING_MAX_CHARS - head.length - tail.length;
  const shown = label.length > room ? `${label.slice(0, Math.max(0, room - 1))}…` : label;
  return `${head}${shown}${tail}`;
}

function capWarnings(warnings: string[]): string[] {
  if (warnings.length <= WARNINGS_MAX) return warnings;
  const rest = warnings.length - (WARNINGS_MAX - 1);
  return [
    ...warnings.slice(0, WARNINGS_MAX - 1),
    `…and ${rest} more required checkboxes lost their emailOptIn binding the same way.`,
  ];
}

const HEX_OR_ALPHA = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const SLUG = /^\/[a-z0-9\-_/]*$/;
const INTERNAL_TARGET = /^[#/][A-Za-z0-9\-_/#]*$/;

// ── Internal typed failure (caught at the compile boundary) ──────────────────
class CompileFail extends Error {
  constructor(
    readonly code: WebsiteCompileErrorCode,
    readonly detail?: string,
  ) {
    super(code);
  }
}
const fail = (code: WebsiteCompileErrorCode, detail?: string): never => {
  throw new CompileFail(code, detail);
};

const isHttps = (u: string) => /^https:\/\//i.test(u);
/**
 * Strict color whitelist (hex / "transparent") — exported so the AI APPLY
 * lanes (canvas background, website setStyle) enforce the same rule as this
 * create lane; anything looser is a CSS-injection vector into sent email
 * HTML / the guest site.
 */
export const isColor = (c: string): boolean => c === "transparent" || HEX_OR_ALPHA.test(c);
const stripTags = (s: string) => s.replace(/<[^>]*>/g, "").trim();

// ── Per-page compile context ─────────────────────────────────────────────────
type Ctx = {
  nodes: Record<string, N>;
  mint: () => string;
  body: string;
  primary: string;
  languages: Set<string>;
  /** Resolved theme palette (strict 6-hex, preset/base-defaulted) for the
   *  `background` token; mirrors buildThemeConfig's fallback chain. */
  palette: { primary: string; secondary: string; background: string };
  /** theme.displayScale === "display" → oversized fluid hero header */
  display: boolean;
  /** Localized submit labels for DSL sections that name none. */
  defaults: WebsiteCompileDefaults;
  /** Notes for the tool result (see WebsiteCompileOk.warnings). */
  warnings: string[];
};

/**
 * Submit labels this compiler falls back to. The DSL is authored by Velo,
 * which routinely omits `submitText` — and the runtime has no default of its
 * own (button.tsx only substitutes one for the allowUpdate case), so an
 * omission used to ship a live button with NO TEXT AT ALL.
 *
 * Passed in rather than translated here: the compiler stays i18n-free (its
 * decompiler counterpart is ported to attendee-app-backend against pinned
 * fixtures), and the caller has the cockpit's `websiteBuilderDefaults`.
 */
export type WebsiteCompileDefaults = {
  /** DataInput sections (`kind: "datainput"`) — websiteBuilderDefaults.submitButton. */
  submitText: string;
  /** `kind: "register"` — websiteBuilderDefaults.createAccount. */
  registerSubmitText: string;
  /** `kind: "login"` — websiteBuilderDefaults.signIn. */
  loginSubmitText: string;
};

/** Sanitize + locale-validate a LangText; returns the cleaned value
 *  (always a string or a per-locale map — never null/undefined). */
function prepLang(v: LangText, ctx: Ctx): string | Record<string, string> {
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return stripTags(v);
  const out: Record<string, string> = {};
  for (const [locale, raw] of Object.entries(v)) {
    const full = normalizeToFullLocale(locale);
    if (!full) fail("unknownLocale", locale);
    if (!ctx.languages.has(full!)) fail("unknownLocale", `${locale} not in languages`);
    out[full!] = stripTags(String(raw ?? ""));
  }
  return out;
}

/** Resolve a (cleaned) LangText to the primary-language plain string. */
function primStr(v: LangText, ctx: Ctx): string {
  const c = prepLang(v, ctx);
  if (typeof c === "string") return c;
  return c[ctx.primary] ?? Object.values(c)[0] ?? "";
}

function requireText(v: LangText, ctx: Ctx, what: string): string {
  const s = primStr(v, ctx);
  if (!s) fail("missingText", what);
  return s;
}

/** Map a button spec to Button link props; validates url/target. */
function linkProps(spec: WebsiteButtonSpec | undefined): N {
  if (!spec) return { linkType: "internal", linkUrl: "" };
  if (spec.url) {
    if (!isHttps(spec.url)) fail("invalidUrl", spec.url);
    return { linkType: "external", linkUrl: spec.url, fixedNavigation: false };
  }
  if (spec.target) {
    if (!INTERNAL_TARGET.test(spec.target)) fail("invalidUrl", spec.target);
    return { linkType: "internal", linkUrl: spec.target, fixedNavigation: true };
  }
  return { linkType: "internal", linkUrl: "" };
}

function checkColor(c: string | undefined): string | undefined {
  if (c === undefined) return undefined;
  if (!isColor(c)) fail("invalidColor", c);
  return c;
}

// ── WS2 model-facing style knobs (compile to the WS1 node props) ─────────────

const REVEAL_VALUES = new Set<string>(["none", "fade", "rise"]);
const BUTTON_VARIANTS = new Set<string>(["solid", "pill", "ghost", "elevated"]);
const HERO_OVERLAYS = new Set<string>(["none", "scrim", "scrim-strong"]);

/** Scrim overlay colors — hex-alpha like the stock "#00000080" default
 *  (black @ 50% / 68%). Shared with the decompiler so the overlay round-trips. */
export const OVERLAY_SCRIM = "#00000080";
export const OVERLAY_SCRIM_STRONG = "#000000ad";

/** Section `reveal` node prop — emitted ONLY for real motion; absent/"none"
 *  emits nothing so pre-WS2 docs stay byte-identical. */
function revealProp(s: { reveal?: WebsiteReveal }): N {
  if (s.reveal === undefined || s.reveal === "none") return {};
  if (!REVEAL_VALUES.has(s.reveal)) fail("invalidStructure", `reveal "${s.reveal}"`);
  return { reveal: s.reveal };
}

/** Button `variant` node prop — emitted only when the spec sets one. */
function variantProp(spec: WebsiteButtonSpec | undefined): N {
  const v = spec?.variant;
  if (v === undefined) return {};
  if (!BUTTON_VARIANTS.has(v)) fail("invalidStructure", `button variant "${v}"`);
  return { variant: v };
}

/** Deterministic light wash of a strict-6-hex color (12% over white). */
function tintOf(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const mix = (c: number) => Math.round(255 - (255 - c) * 0.12).toString(16).padStart(2, "0");
  return `#${mix((n >> 16) & 255)}${mix((n >> 8) & 255)}${mix(n & 255)}`;
}

/** Resolve a theme-palette background token to a concrete hex color. */
function resolveBackground(token: WebsiteBackgroundToken | undefined, ctx: Ctx): string | undefined {
  if (token === undefined) return undefined;
  switch (token) {
    case "surface": return ctx.palette.background;
    case "tint": return tintOf(ctx.palette.primary);
    case "contrast": return ctx.palette.secondary;
    default: return fail("invalidStructure", `background "${token}"`) as never;
  }
}

// ── Section builders (mirror temp.ts welcome()/register() node shapes) ────────

function buildHero(s: Extract<WebsiteSection, { kind: "hero" }>, ctx: Ctx): string {
  const sec = ctx.mint();
  const hl = ctx.mint(), hd = ctx.mint(), tx = ctx.mint(), b1 = ctx.mint(), b2 = ctx.mint();
  const header = requireText(s.header, ctx, "hero.header");
  if (s.imageSrc && !isHttps(s.imageSrc)) fail("invalidUrl", s.imageSrc);
  const layout = s.imageLayout ?? "background";
  const bg = checkColor(s.backgroundColor) ?? resolveBackground(s.background, ctx) ?? "#f5f5f5";
  // Scrim overlay: non-"none" switches the flat fill to the WS1 bottom-anchored
  // gradient; the strength is carried in backgroundOverlayColor's alpha (the
  // "none" color equals the stock default, so old DSLs compile byte-identical).
  const overlay = s.overlay ?? "none";
  if (!HERO_OVERLAYS.has(overlay)) fail("invalidStructure", `overlay "${overlay}"`);
  const overlayColor = overlay === "scrim-strong" ? OVERLAY_SCRIM_STRONG : OVERLAY_SCRIM;
  const overlayProps: N = overlay === "none" ? {} : { overlayStyle: "scrim-bottom" };
  // Background video (https-validated like imageSrc); absent = no props emitted.
  const videoProps: N = {};
  if (s.videoSrc !== undefined) {
    if (!isHttps(s.videoSrc)) fail("invalidUrl", s.videoSrc);
    videoProps.backgroundType = "video";
    videoProps.videoSrc = s.videoSrc;
    videoProps.videoLoop = true;
    if (s.videoPoster !== undefined) {
      if (!isHttps(s.videoPoster)) fail("invalidUrl", s.videoPoster);
      videoProps.videoPosterSrc = s.videoPoster;
    }
  }
  // displayScale "display" → oversized fluid header (regular = stock text-2xl).
  const headerSize = ctx.display ? "text-7xl" : "text-2xl";
  // White text only reads on the dark photo overlay (background layout). On a
  // light/transparent section background it would be invisible — let the theme
  // color apply (omit the override).
  const heroColor: string | undefined = layout === "background" ? "#FFFFFF" : undefined;
  const sideImage = layout === "left" || layout === "right";
  // Hero gates background media AND the overlay div on isVisibleImage — the new
  // video/scrim opt-ins must flip it on for a background-layout hero or they
  // would never render. Without them the historical `sideImage` value is kept
  // (pre-WS2 DSLs compile byte-identical).
  const mediaVisible =
    sideImage || s.videoSrc !== undefined || (overlay !== "none" && layout === "background");
  ctx.nodes[sec] = {
    type: { resolvedName: "Hero" }, isCanvas: false,
    props: {
      sectionHeight: s.sectionHeight ?? 80, contentAlignment: "center", buttonAlign: "center",
      padding: "px-6 wb-md:px-8 wb-lg:px-12", textSpacing: "space-y-3", buttonTextSpacing: "mt-8", isVisible: true,
      isVisibleHeadline: !!s.headline, textHeadline: primStr(s.headline, ctx), fontSizeHeadline: "text-xl", textAlignHeadline: "center", headlineColor: heroColor,
      isVisibleHeader: true, textHeader: header, fontSizeHeader: headerSize, textAlignHeader: "center", headerColor: heroColor,
      isVisibleText: !!s.text, textText: primStr(s.text, ctx), fontSizeText: "text-md", textAlignText: "center", textColor: heroColor,
      secondaryButtonSize: "medium", secondaryButtonText: primStr(s.secondaryButton?.text, ctx), isVisibleSecondaryButton: !!s.secondaryButton, secondaryButtonBorder: true, secondaryButtonUnderline: false,
      primaryButtonSize: "medium", primaryButtonBorder: true, primaryButtonText: primStr(s.primaryButton?.text, ctx), isVisiblePrimaryButton: !!s.primaryButton, primaryButtonUnderline: false,
      imageLayout: layout, backgroundOverlayColor: overlayColor, isVisibleImage: mediaVisible,
      imageSrc: s.imageSrc ?? `${A}/shared/hero.jpg`, backgroundColor: bg,
      ...overlayProps, ...videoProps, ...revealProp(s),
    },
    displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
    parent: ctx.body, hidden: false, nodes: [],
    linkedNodes: { headline: hl, header: hd, text: tx, buttonOne: b1, buttonTwo: b2 },
  };
  ctx.nodes[hl] = tc(sec, { isVisible: !!s.headline, text: prepLang(s.headline, ctx), fontSize: "text-xl", textAlign: "center", displayName: "headline", color: heroColor, header: true });
  ctx.nodes[hd] = tc(sec, { isVisible: true, text: prepLang(s.header, ctx), fontSize: headerSize, ...(ctx.display ? { fluid: true } : {}), textAlign: "center", displayName: "header", color: heroColor, header: true });
  ctx.nodes[tx] = tc(sec, { isVisible: !!s.text, text: prepLang(s.text, ctx), fontSize: "text-md", textAlign: "center", displayName: "text", color: heroColor, header: false });
  ctx.nodes[b1] = btn(sec, { type: "secondary", text: prepLang(s.secondaryButton?.text, ctx), isVisible: !!s.secondaryButton, displayName: "buttonOne", ...linkProps(s.secondaryButton), ...variantProp(s.secondaryButton) });
  ctx.nodes[b2] = btn(sec, { type: "primary", text: prepLang(s.primaryButton?.text, ctx), isVisible: !!s.primaryButton, displayName: "buttonTwo", ...linkProps(s.primaryButton), ...variantProp(s.primaryButton) });
  return sec;
}

function buildText(s: Extract<WebsiteSection, { kind: "text" }>, ctx: Ctx): string {
  const sec = ctx.mint();
  const hl = ctx.mint(), hd = ctx.mint(), tx = ctx.mint(), b1 = ctx.mint(), b2 = ctx.mint();
  ctx.nodes[sec] = {
    type: { resolvedName: "TextSection" }, isCanvas: false,
    props: {
      buttonAlign: "center", padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3", buttonTextSpacing: "mt-8", isVisible: true,
      layoutWidth: s.layoutWidth ?? "100%",
      backgroundColor: checkColor(s.backgroundColor) ?? resolveBackground(s.background, ctx) ?? "transparent",
      isVisibleHeadline: !!s.headline, textHeadline: primStr(s.headline, ctx), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
      isVisibleHeader: true, textHeader: requireText(s.header, ctx, "text.header"), fontSizeHeader: "text-xl", textAlignHeader: "center",
      isVisibleText: true, textText: requireText(s.text, ctx, "text.text"), fontSizeText: "text-md", textAlignText: "center",
      secondaryButtonSize: "medium", secondaryButtonText: "", isVisibleSecondaryButton: false, secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
      primaryButtonSize: "medium", primaryButtonText: "", isVisiblePrimaryButton: false, primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      ...revealProp(s),
    },
    displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
    parent: ctx.body, hidden: false, nodes: [],
    linkedNodes: { headline: hl, header: hd, text: tx, buttonOne: b1, buttonTwo: b2 },
  };
  ctx.nodes[hl] = tc(sec, { isVisible: !!s.headline, text: prepLang(s.headline, ctx), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true });
  ctx.nodes[hd] = tc(sec, { isVisible: true, text: prepLang(s.header, ctx), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true });
  ctx.nodes[tx] = tc(sec, { isVisible: true, text: prepLang(s.text, ctx), fontSize: "text-md", textAlign: "center", displayName: "text", header: false });
  ctx.nodes[b1] = btn(sec, { type: "secondary", text: "", isVisible: false, displayName: "buttonOne" });
  ctx.nodes[b2] = btn(sec, { text: "", isVisible: false, displayName: "buttonTwo" });
  return sec;
}

function buildImage(s: Extract<WebsiteSection, { kind: "image" }>, ctx: Ctx): string {
  if (!s.imageSrc || !isHttps(s.imageSrc)) fail("invalidUrl", s.imageSrc ?? "(missing image)");
  const sec = ctx.mint();
  const hl = ctx.mint(), hd = ctx.mint(), tx = ctx.mint(), bt = ctx.mint(), img = ctx.mint();
  const aspect = s.imageAspectRatio ?? "aspect-video";
  ctx.nodes[sec] = {
    type: { resolvedName: "Image" }, isCanvas: false,
    props: {
      sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN,
      padding: PAD, textSpacing: "space-y-3", buttonTextSpacing: "mt-6", isVisible: true,
      isVisibleHeadline: !!s.headline, textHeadline: primStr(s.headline, ctx), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
      isVisibleHeader: !!s.header, textHeader: primStr(s.header, ctx), fontSizeHeader: "text-xl", textAlignHeader: "center",
      isVisibleText: !!s.text, textText: primStr(s.text, ctx), fontSizeText: "text-md", textAlignText: "center",
      buttonSize: "medium", buttonText: "", isVisibleButton: false, buttonAlign: "center", buttonBorder: true, buttonUnderline: false,
      imageRounded: "rounded-lg", imageWidth: "50%", imagePosition: s.inversed ? "left/top" : "right/bottom",
      isVisibleImage: true, imageSrc: s.imageSrc, imageAspectRatio: aspect,
      ...revealProp(s),
    },
    displayName: "Image", custom: { icon: "IconSection", preview: pv("Image-preview") },
    parent: ctx.body, hidden: false, nodes: [],
    linkedNodes: { headline: hl, header: hd, text: tx, button: bt, image: img },
  };
  ctx.nodes[hl] = tc(sec, { isVisible: !!s.headline, text: prepLang(s.headline, ctx), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true });
  ctx.nodes[hd] = tc(sec, { isVisible: !!s.header, text: prepLang(s.header, ctx), fontSize: "text-xl", textAlign: "center", displayName: "header", header: true });
  ctx.nodes[tx] = tc(sec, { isVisible: !!s.text, text: prepLang(s.text, ctx), fontSize: "text-md", textAlign: "center", displayName: "text", header: false });
  ctx.nodes[bt] = btn(sec, { text: "", isVisible: false, displayName: "button" });
  ctx.nodes[img] = {
    type: { resolvedName: "ImageChild" }, isCanvas: false,
    props: { src: s.imageSrc, isVisible: true, aspectRatio: aspect, rounded: "rounded-lg", displayName: "ImageChild", width: "w-full wb-lg:w-1/2", canDuplicate: false },
    displayName: "ImageChild", custom: { icon: "IconSection" },
    parent: sec, hidden: false, nodes: [], linkedNodes: {},
  };
  return sec;
}

type NormField = { name: string; label: LangText; required: boolean; gridArea: string };

/** Resolve a register/datainput field spec (string shorthand OR object) and
 *  validate the bound name is a real event field (§0b.6: OMIT overwriteType so
 *  the input inherits the event field's dataType). */
function normField(spec: RegisterFieldSpec, ctx: Ctx, fieldNames: Set<string>, last: boolean): NormField {
  const o = typeof spec === "string" ? { name: spec } : spec;
  if (!o.name || !fieldNames.has(o.name)) fail("unknownRegisterField", o.name || "(missing)");
  const full = o.fullWidth ?? last;
  return {
    name: o.name,
    label: o.label !== undefined ? prepLang(o.label, ctx) : o.name,
    required: o.required ?? (o.name === "email"),
    gridArea: full ? "col-span-12" : "col-span-6",
  };
}

function buildRegister(
  s: Extract<WebsiteSection, { kind: "register" }>,
  ctx: Ctx,
  fieldNames: Set<string>,
): string {
  if (!Array.isArray(s.fields) || s.fields.length === 0) fail("noRegisterFields");
  if (s.fields.length > MAX_REGISTER_FIELDS) fail("tooManyRegisterFields", `max ${MAX_REGISTER_FIELDS}`);
  const norm = s.fields.map((spec, i) => normField(spec, ctx, fieldNames, i === s.fields.length - 1));
  const sec = ctx.mint(), rc = ctx.mint();
  const hl = ctx.mint(), hd = ctx.mint(), tx = ctx.mint(), primary = ctx.mint(), secondary = ctx.mint();
  const fieldIds = norm.map(() => ctx.mint());
  ctx.nodes[sec] = {
    type: { resolvedName: "Register" }, isCanvas: false, props: { ...AUTH_WRAPPER_PROPS, ...revealProp(s) },
    displayName: "Register", custom: { icon: "IconUserPlus", preview: pv("Register-preview") },
    parent: ctx.body, hidden: false, nodes: [], linkedNodes: { registerChild: rc },
  };
  ctx.nodes[rc] = {
    type: { resolvedName: "RegisterChild" }, isCanvas: true,
    props: { ...AUTH_CHILD_PROPS, fieldCount: norm.length },
    displayName: "RegisterChild", custom: { icon: "IconUserPlus", allowedComponents: ["FormInputChild"] },
    parent: sec, hidden: false, nodes: fieldIds,
    linkedNodes: { headline: hl, header: hd, text: tx, primary, secondary },
  };
  ctx.nodes[hl] = tc(rc, { isVisible: !!s.headline, text: prepLang(s.headline, ctx), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true });
  ctx.nodes[hd] = tc(rc, { isVisible: true, text: prepLang(s.header ?? "", ctx), fontSize: "text-3xl", textAlign: "left", displayName: "header", color: "#ffffff", header: true });
  ctx.nodes[tx] = tc(rc, { isVisible: !!s.text, text: prepLang(s.text, ctx), fontSize: "text-md", textAlign: "left", displayName: "text", color: "#cbd5e1", header: false });
  norm.forEach((f, i) => {
    ctx.nodes[fieldIds[i]] = fi(rc, {
      label: f.label, requiredField: f.required, inputFieldVariable: f.name,
      gridArea: f.gridArea, changeable: true,
    });
  });
  ctx.nodes[primary] = btn(rc, { linkType: "internal", linkUrl: "", text: prepLang(s.submitText || ctx.defaults.registerSubmitText, ctx), widthMode: "full", buttonType: "submit", displayName: "buttonOne", fixedNavigation: false });
  ctx.nodes[secondary] = btn(rc, { linkType: "internal", type: "secondary", linkUrl: "/login", text: "", widthMode: "full", underline: true, border: false, isVisible: false, displayName: "buttonTwo", fixedNavigation: true });
  return sec;
}

// ── Phase 3 full vocabulary ──────────────────────────────────────────────────

type TextTrio = { headline?: LangText; header?: LangText; text?: LangText };

/** Section node's plain `text*` props (primary-language strings, §0b.8). */
function trioProps(ctx: Ctx, v: TextTrio, align = "center"): N {
  return {
    isVisibleHeadline: !!primStr(v.headline, ctx), textHeadline: primStr(v.headline, ctx), fontSizeHeadline: "text-lg", textAlignHeadline: align,
    isVisibleHeader: !!primStr(v.header, ctx), textHeader: primStr(v.header, ctx), fontSizeHeader: "text-xl", textAlignHeader: align,
    isVisibleText: !!primStr(v.text, ctx), textText: primStr(v.text, ctx), fontSizeText: "text-md", textAlignText: align,
  };
}

/** Mirrored LangText child slots for headline/header/text (in sync, §0b.8). */
function trioNodes(ctx: Ctx, parent: string, v: TextTrio, align = "center"): { hl: string; hd: string; tx: string } {
  const hl = ctx.mint(), hd = ctx.mint(), tx = ctx.mint();
  ctx.nodes[hl] = tc(parent, { isVisible: !!primStr(v.headline, ctx), text: prepLang(v.headline, ctx), fontSize: "text-lg", textAlign: align, displayName: "headline", header: true });
  ctx.nodes[hd] = tc(parent, { isVisible: !!primStr(v.header, ctx), text: prepLang(v.header, ctx), fontSize: "text-xl", textAlign: align, displayName: "header", header: true });
  ctx.nodes[tx] = tc(parent, { isVisible: !!primStr(v.text, ctx), text: prepLang(v.text, ctx), fontSize: "text-md", textAlign: align, displayName: "text", header: false });
  return { hl, hd, tx };
}

function requireItems(items: unknown[] | undefined, itemType: "object" | "string" = "object"): void {
  if (!Array.isArray(items) || items.length === 0) fail("noItems");
  if (items!.length > MAX_SECTION_ITEMS) fail("tooManyItems", `max ${MAX_SECTION_ITEMS}`);
  // Reject wrong-shaped entries up front — otherwise `it.title` etc. throws a
  // raw TypeError that escapes the structured-error boundary and crashes the
  // handler. Gallery items are bare https-URL STRINGS, every other section's
  // items are objects (the object-only check used to reject every gallery).
  const bad = itemType === "string"
    ? items!.some((it) => typeof it !== "string")
    : items!.some((it) => it == null || typeof it !== "object");
  if (bad) fail("invalidStructure", itemType === "string" ? "non-string item" : "null or non-object item");
}

function buildAgenda(s: Extract<WebsiteSection, { kind: "agenda" }>, ctx: Ctx): string {
  requireItems(s.items);
  requireText(s.header, ctx, "agenda.header");
  const sec = ctx.mint();
  const { hl, hd, tx } = trioNodes(ctx, sec, s);
  const childIds = s.items.map(() => ctx.mint());
  ctx.nodes[sec] = {
    type: { resolvedName: "Agenda" }, isCanvas: true,
    props: { padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-4", isVisible: true, ...trioProps(ctx, s), ...revealProp(s) },
    displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
    parent: ctx.body, hidden: false, nodes: childIds, linkedNodes: { headline: hl, header: hd, text: tx },
  };
  s.items.forEach((it, i) => {
    requireText(it.title, ctx, "agenda.item.title");
    ctx.nodes[childIds[i]] = {
      type: { resolvedName: "AgendaChild" }, isCanvas: false,
      props: {
        isVisibleText1: !!primStr(it.time, ctx), textText1: primStr(it.time, ctx), fontSizeText1: "text-sm", textAlignText1: "left",
        isVisibleText2: true, textText2: primStr(it.title, ctx), fontSizeText2: "text-sm", textAlignText2: "left",
        isVisibleText3: !!primStr(it.speaker, ctx), textText3: primStr(it.speaker, ctx), fontSizeText3: "text-sm", textAlignText3: "left",
        isVisibleText: !!primStr(it.desc, ctx), textText: primStr(it.desc, ctx), fontSizeText: "text-sm", textAlignText: "left", border: true,
      },
      displayName: "AgendaChild", custom: { icon: "IconCalendar" }, parent: sec, hidden: false, nodes: [], linkedNodes: {},
    };
  });
  return sec;
}

function buildSpeaker(s: Extract<WebsiteSection, { kind: "speaker" }>, ctx: Ctx): string {
  requireItems(s.items);
  requireText(s.header, ctx, "speaker.header");
  for (const it of s.items) if (it.imageUrl && !isHttps(it.imageUrl)) fail("invalidUrl", it.imageUrl);
  const sec = ctx.mint();
  const { hl, hd, tx } = trioNodes(ctx, sec, s);
  const childIds = s.items.map(() => ctx.mint());
  ctx.nodes[sec] = {
    type: { resolvedName: "Speaker" }, isCanvas: true,
    props: { padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3", isVisible: true, speakersPosition: "left/top", speakersWidth: "50%", ...trioProps(ctx, s), ...revealProp(s) },
    displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
    parent: ctx.body, hidden: false, nodes: childIds, linkedNodes: { headline: hl, header: hd, text: tx },
  };
  s.items.forEach((it, i) => {
    requireText(it.name, ctx, "speaker.item.name");
    ctx.nodes[childIds[i]] = {
      type: { resolvedName: "SpeakerChild" }, isCanvas: false,
      props: {
        isVisible: true, imageUrl: it.imageUrl ?? SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
        isVisibleName: true, textName: primStr(it.name, ctx), fontSizeName: "text-xl", textAlignName: "left",
        isVisibleOccupation: !!primStr(it.occupation, ctx), textOccupation: primStr(it.occupation, ctx), fontSizeOccupation: "text-md", textAlignOccupation: "left",
        icons: SPEAKER_ICONS, isVisibleIcon: true,
      },
      displayName: "SpeakerChild", custom: { icon: "IconMicrophone" }, parent: sec, hidden: false, nodes: [], linkedNodes: {},
    };
  });
  return sec;
}

function buildTestimonials(s: Extract<WebsiteSection, { kind: "testimonials" }>, ctx: Ctx): string {
  requireItems(s.items);
  requireText(s.header, ctx, "testimonials.header");
  for (const it of s.items) if (it.imageUrl && !isHttps(it.imageUrl)) fail("invalidUrl", it.imageUrl);
  const sec = ctx.mint();
  const { hl, hd, tx } = trioNodes(ctx, sec, s);
  const childIds = s.items.map(() => ctx.mint());
  ctx.nodes[sec] = {
    type: { resolvedName: "Testimonials" }, isCanvas: true,
    props: { padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: "space-y-3", testimonialPosition: "left/top", testimonialWidth: "50%", ...trioProps(ctx, s), ...revealProp(s) },
    displayName: "Testimonials", custom: { icon: "IconQuote", preview: pv("Testimonials-preview"), allowedComponents: ["TestimonialsChild"] },
    parent: ctx.body, hidden: false, nodes: childIds, linkedNodes: { headline: hl, header: hd, text: tx },
  };
  s.items.forEach((it, i) => {
    requireText(it.quote, ctx, "testimonials.item.quote");
    requireText(it.name, ctx, "testimonials.item.name");
    ctx.nodes[childIds[i]] = {
      type: { resolvedName: "TestimonialsChild" }, isCanvas: false,
      props: {
        isVisible: true, imageUrl: SAMPLE_SPONSOR_LOGOS[i % SAMPLE_SPONSOR_LOGOS.length].src, imageUrlBottom: it.imageUrl ?? SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length],
        isVisibleImageTop: true, imageBottomBackground: "transparent", isVisibleImageBottom: true,
        isVisibleContent: true, textContent: primStr(it.quote, ctx), fontSizeContent: "text-md", textAlignContent: "left",
        isVisibleName: true, textName: primStr(it.name, ctx), fontSizeName: "text-xl", textAlignName: "left",
        isVisibleOccupation: !!primStr(it.occupation, ctx), textOccupation: primStr(it.occupation, ctx), fontSizeOccupation: "text-md", textAlignOccupation: "left",
      },
      displayName: "TestimonialsChild", custom: { icon: "IconThumbUp" }, parent: sec, hidden: false, nodes: [], linkedNodes: {},
    };
  });
  return sec;
}

function buildNumbers(s: Extract<WebsiteSection, { kind: "numbers" }>, ctx: Ctx): string {
  requireItems(s.items);
  requireText(s.header, ctx, "numbers.header");
  const sec = ctx.mint();
  const { hl, hd, tx } = trioNodes(ctx, sec, s);
  const childIds = s.items.map(() => ctx.mint());
  ctx.nodes[sec] = {
    type: { resolvedName: "Numbers" }, isCanvas: true,
    props: { sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, padding: PAD, textSpacing: "space-y-3", isVisible: true, numbersPosition: "left/top", numbersWidth: "50%", ...trioProps(ctx, s), ...revealProp(s) },
    displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
    parent: ctx.body, hidden: false, nodes: childIds, linkedNodes: { headline: hl, header: hd, text: tx },
  };
  s.items.forEach((it, i) => {
    requireText(it.number, ctx, "numbers.item.number");
    requireText(it.label, ctx, "numbers.item.label");
    ctx.nodes[childIds[i]] = {
      type: { resolvedName: "NumbersChild" }, isCanvas: false,
      props: {
        isVisible: true,
        isVisibleNumber: true, textNumber: primStr(it.number, ctx), fontSizeNumber: "text-lg", textAlignNumber: "center",
        isVisibleDescription: true, textDescription: primStr(it.label, ctx), fontSizeDescription: "text-md", textAlignDescription: "center",
      },
      displayName: "NumbersChild", custom: { icon: "IconNumber" }, parent: sec, hidden: false, nodes: [], linkedNodes: {},
    };
  });
  return sec;
}

function buildFaq(s: Extract<WebsiteSection, { kind: "faq" }>, ctx: Ctx): string {
  requireItems(s.items);
  requireText(s.header, ctx, "faq.header");
  const sec = ctx.mint();
  const { hl, hd, tx } = trioNodes(ctx, sec, s);
  const childIds = s.items.map(() => ctx.mint());
  ctx.nodes[sec] = {
    type: { resolvedName: "FaQ" }, isCanvas: true,
    props: { padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-4", isVisible: true, ...trioProps(ctx, s), ...revealProp(s) },
    displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
    parent: ctx.body, hidden: false, nodes: childIds, linkedNodes: { headline: hl, header: hd, text: tx },
  };
  s.items.forEach((it, i) => {
    requireText(it.question, ctx, "faq.item.question");
    requireText(it.answer, ctx, "faq.item.answer");
    ctx.nodes[childIds[i]] = {
      type: { resolvedName: "FaQChild" }, isCanvas: false,
      props: {
        isVisibleQuestion: true, textQuestion: primStr(it.question, ctx), fontSizeQuestion: "text-sm", textAlignQuestion: "left",
        isVisibleAnswer: true, textAnswer: primStr(it.answer, ctx), fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
      },
      displayName: "FaQChild", custom: { icon: "IconMessageQuestion" }, parent: sec, hidden: false, nodes: [], linkedNodes: {},
    };
  });
  return sec;
}

function buildGallery(s: Extract<WebsiteSection, { kind: "gallery" }>, ctx: Ctx): string {
  requireItems(s.images, "string");
  for (const url of s.images) if (!isHttps(url)) fail("invalidUrl", url);
  const sec = ctx.mint();
  const { hl, hd, tx } = trioNodes(ctx, sec, s);
  const sBtn = ctx.mint(), pBtn = ctx.mint();
  const imgIds = s.images.map(() => ctx.mint());
  ctx.nodes[sec] = {
    type: { resolvedName: "Gallery" }, isCanvas: true,
    props: {
      padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, buttonAlign: "center", textSpacing: "space-y-3", buttonTextSpacing: "mt-6",
      secondaryButtonSize: "medium", secondaryButtonText: "", isVisibleSecondaryButton: false, secondaryButtonBorder: true, secondaryButtonUnderline: false,
      primaryButtonSize: "medium", primaryButtonText: "", isVisiblePrimaryButton: false, primaryButtonBorder: true, primaryButtonUnderline: false,
      galleryPosition: "left/top", galleryWidth: "100%", ...trioProps(ctx, s), ...revealProp(s),
    },
    displayName: "Gallery", custom: { icon: "IconPhoto", preview: pv("Gallery-preview"), allowedComponents: ["ImageChild"] },
    parent: ctx.body, hidden: false, nodes: imgIds, linkedNodes: { headline: hl, header: hd, text: tx, secondary: sBtn, primary: pBtn },
  };
  ctx.nodes[sBtn] = btn(sec, { type: "secondary", text: "", isVisible: false, displayName: "ButtonChild" });
  ctx.nodes[pBtn] = btn(sec, { text: "", isVisible: false, displayName: "ButtonChild" });
  s.images.forEach((url, i) => {
    ctx.nodes[imgIds[i]] = {
      type: { resolvedName: "ImageChild" }, isCanvas: false,
      props: { src: url, isVisible: true, aspectRatio: "aspect-video", displayName: "ImageChild", width: "w-full", canDuplicate: true },
      displayName: "ImageChild", custom: { icon: "IconSection" }, parent: sec, hidden: false, nodes: [], linkedNodes: {},
    };
  });
  return sec;
}

function buildCountdown(s: Extract<WebsiteSection, { kind: "countdown" }>, ctx: Ctx): string {
  requireText(s.header, ctx, "countdown.header");
  const sec = ctx.mint();
  const { hl, hd, tx } = trioNodes(ctx, sec, s);
  const button = ctx.mint();
  ctx.nodes[sec] = {
    type: { resolvedName: "Countdown" }, isCanvas: false,
    props: {
      sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, padding: PAD, isVisible: true,
      textSpacing: "space-y-3", buttonTextSpacing: "mt-6", buttonAlign: "center",
      buttonSize: "medium", buttonText: "", isVisibleButton: false, buttonBorder: true, buttonUnderline: false,
      countdownWidth: "100%", countdownPosition: "right/bottom", countdownVariant: "separated",
      isVisiblCountdown: true, countdownSizeDate: "text-xl", countdownSize: "text-base", ...trioProps(ctx, s), ...revealProp(s),
    },
    displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
    parent: ctx.body, hidden: false, nodes: [], linkedNodes: { headline: hl, header: hd, text: tx, button },
  };
  ctx.nodes[button] = btn(sec, { text: "", isVisible: false, displayName: "ButtonChild" });
  return sec;
}

function buildMap(s: Extract<WebsiteSection, { kind: "map" }>, ctx: Ctx): string {
  requireText(s.header, ctx, "map.header");
  const sec = ctx.mint();
  const { hl, hd, tx } = trioNodes(ctx, sec, s);
  const button = ctx.mint();
  ctx.nodes[sec] = {
    type: { resolvedName: "Map" }, isCanvas: false,
    props: {
      sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, padding: PAD, isVisible: true,
      textSpacing: "space-y-3", buttonTextSpacing: "mt-6",
      buttonSize: "medium", buttonText: "", isVisibleButton: false, buttonBorder: true, buttonUnderline: false, buttonAlign: "center",
      mapRounded: "rounded-lg", mapWidth: "50%", mapPosition: "right/bottom",
      mapZoom: 10, mapAspectRatio: "aspect-video", isVisibleMap: true, mapLocation: primStr(s.location, ctx) || "Berlin, Germany", ...trioProps(ctx, s), ...revealProp(s),
    },
    displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
    parent: ctx.body, hidden: false, nodes: [], linkedNodes: { headline: hl, header: hd, text: tx, button },
  };
  ctx.nodes[button] = btn(sec, { text: "", isVisible: false, displayName: "button" });
  return sec;
}

function buildLogin(s: Extract<WebsiteSection, { kind: "login" }>, ctx: Ctx): string {
  const sec = ctx.mint(), lc = ctx.mint();
  const hl = ctx.mint(), hd = ctx.mint(), tx = ctx.mint();
  const loginForm = ctx.mint(), codeForm = ctx.mint(), primary = ctx.mint(), secondary = ctx.mint();
  ctx.nodes[sec] = {
    type: { resolvedName: "Login" }, isCanvas: false, props: { ...AUTH_WRAPPER_PROPS, ...revealProp(s) },
    displayName: "Login", custom: { icon: "IconForms", preview: pv("Login-preview") },
    parent: ctx.body, hidden: false, nodes: [], linkedNodes: { loginChild: lc },
  };
  ctx.nodes[lc] = {
    type: { resolvedName: "LoginChild" }, isCanvas: true, props: { ...AUTH_CHILD_PROPS },
    displayName: "LoginChild", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
    parent: sec, hidden: false, nodes: [],
    linkedNodes: { headline: hl, header: hd, text: tx, loginForm, codeForm, primary, secondary },
  };
  // headline kept hidden+empty (the login DSL has only header) — mirrors temp.ts
  // login(); otherwise the header text would render twice.
  ctx.nodes[hl] = tc(lc, { isVisible: false, text: prepLang(undefined, ctx), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true });
  ctx.nodes[hd] = tc(lc, { isVisible: true, text: prepLang(s.header ?? "", ctx), fontSize: "text-xl", textAlign: "left", displayName: "header", header: true });
  ctx.nodes[tx] = tc(lc, { isVisible: !!primStr(s.text, ctx), text: prepLang(s.text, ctx), fontSize: "text-md", textAlign: "left", displayName: "text", header: false });
  ctx.nodes[loginForm] = fi(lc, { label: "Email", requiredField: true, inputFieldVariable: "username", changeable: true });
  ctx.nodes[codeForm] = fi(lc, { label: "Code", requiredField: true, inputFieldVariable: "code", changeable: true });
  ctx.nodes[primary] = btn(lc, { linkType: "internal", linkUrl: "", text: prepLang(s.submitText || ctx.defaults.loginSubmitText, ctx), widthMode: "full", buttonType: "submit", displayName: "buttonOne", fixedNavigation: false });
  // Hidden by default: the DSL login section carries no "don't have an account"
  // label and can't know cross-page whether a /register page exists — an empty
  // visible button would render a broken control.
  ctx.nodes[secondary] = btn(lc, { linkType: "internal", type: "secondary", linkUrl: "/register", text: "", widthMode: "full", underline: true, border: false, isVisible: false, displayName: "buttonTwo", fixedNavigation: true });
  return sec;
}

/** Multi-step post-acceptance data collection. Mirrors temp.ts welcome()'s
 *  DataInput: always 5 fieldsGroup slots (first N populated, rest empty), a
 *  companions slot, an overview slot, a checkboxes slot, and submit/back/next.
 *  Fields are ACCEPTANCE-gated (this collects more data from accepted guests;
 *  use `register` for public signup). */
function buildDataInput(
  s: Extract<WebsiteSection, { kind: "datainput" }>,
  ctx: Ctx,
  fieldNames: Set<string>,
): string {
  if (!Array.isArray(s.steps) || s.steps.length === 0) fail("noSteps");
  if (s.steps.length > MAX_DATAINPUT_STEPS) fail("tooManySteps", `max ${MAX_DATAINPUT_STEPS}`);
  for (const step of s.steps) {
    if (!Array.isArray(step.fields) || step.fields.length === 0) fail("noRegisterFields");
  }
  const sec = ctx.mint();
  const hl = ctx.mint(), hd = ctx.mint(), tx = ctx.mint();
  const fgIds = [0, 1, 2, 3, 4].map(() => ctx.mint());
  const compId = ctx.mint(), ovId = ctx.mint(), cbId = ctx.mint();
  const btnId = ctx.mint(), backId = ctx.mint(), nextId = ctx.mint();
  const ovTitle = ctx.mint(), ovText = ctx.mint(), compField = ctx.mint();

  const multiStep = s.steps.length > 1;
  const stepSections = s.steps.map((_, i) => {
    const keys = [`fields-${i}`];
    if (s.companions && i === s.steps.length - 1) keys.push("companions");
    return keys;
  });
  const stepLabels = multiStep ? s.steps.map((st) => primStr(st.label, ctx) || "") : [];

  ctx.nodes[sec] = {
    type: { resolvedName: "DataInput" }, isCanvas: false,
    props: {
      padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
      backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
      borderSides: ["bottom"], borderRadius: undefined,
      isVisibleHeadline: !!primStr(s.headline, ctx), textHeadline: primStr(s.headline, ctx), fontSizeHeadline: "text-base", textAlignHeadline: "center",
      isVisibleHeader: !!primStr(s.header, ctx), textHeader: primStr(s.header, ctx), fontSizeHeader: "text-lg", textAlignHeader: "center",
      isVisibleText: !!primStr(s.text, ctx), textText: primStr(s.text, ctx), fontSizeText: "text-sm", textAlignText: "center",
      allowUpdate: !!s.allowUpdate,
      multiStepEnabled: multiStep, stepLabels, multiStepMode: "pages",
      overviewEnabled: false, stepSections,
      ...revealProp(s),
    },
    displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
    parent: ctx.body, hidden: false, nodes: [],
    linkedNodes: {
      headline: hl, header: hd, text: tx,
      fieldsGroup0: fgIds[0], fieldsGroup1: fgIds[1], fieldsGroup2: fgIds[2], fieldsGroup3: fgIds[3], fieldsGroup4: fgIds[4],
      companionsGroup: compId, overviewGroup: ovId, checkboxes: cbId,
      button: btnId, backButton: backId, nextButton: nextId,
    },
  };
  ctx.nodes[hl] = tc(sec, { isVisible: !!primStr(s.headline, ctx), text: prepLang(s.headline, ctx), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true });
  ctx.nodes[hd] = tc(sec, { isVisible: !!primStr(s.header, ctx), text: prepLang(s.header, ctx), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true });
  ctx.nodes[tx] = tc(sec, { isVisible: !!primStr(s.text, ctx), text: prepLang(s.text, ctx), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false });

  // Field groups: first N from steps, rest empty (the builder expects 5 slots).
  s.steps.forEach((step, gi) => {
    // The participation question is planted by the SECTION (below), never
    // picked. `state` is a legal public form field, so a DSL author can list
    // it — and compiled as an ordinary field it would get
    // `ACCEPTANCE_VISIBILITY_FILTER`, i.e. a question gated on its own answer,
    // which makes the server delete a submitted Absage. The decompiler drops
    // acceptance names on the way back, so the author could neither see nor
    // repair the duplicate they created. Dropped here for the same reason the
    // field pickers exclude these names.
    const pickedFields = step.fields.filter(
      (spec) => !isAcceptanceFieldName(typeof spec === "string" ? spec : (spec.name ?? "")),
    );
    const norm = pickedFields.map((spec, i) => normField(spec, ctx, fieldNames, i === pickedFields.length - 1));
    const ids = norm.map(() => ctx.mint());
    // AUDIT 2026-08-14 — the participation question, first in step 0.
    //
    // Every field below is gated on `state = ACCEPTED`; without a control to
    // answer that, the runtime falls back to submitting under
    // `acceptanceState`, which the worker drops silently — the Zusage is
    // discarded, no automation runs and nothing reaches the guest history.
    // The DSL lane produced real published sites with exactly that hole.
    // Filter-free on purpose: see `acceptanceFi`.
    const accId = gi === 0 ? ctx.mint() : null;
    const groupIds = accId ? [accId, ...ids] : ids;
    ctx.nodes[fgIds[gi]] = {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: groupIds.length, groupIndex: gi, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: sec, hidden: false, nodes: groupIds, linkedNodes: {},
    };
    if (accId) ctx.nodes[accId] = acceptanceFi(fgIds[gi]);
    norm.forEach((f, i) => {
      ctx.nodes[ids[i]] = fi(fgIds[gi], { label: f.label, requiredField: f.required, inputFieldVariable: f.name, gridArea: f.gridArea, visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER });
    });
  });
  for (let gi = s.steps.length; gi < 5; gi++) ctx.nodes[fgIds[gi]] = emptyFieldsGroup(sec, gi);

  // Companions slot (always present; populated only when requested).
  ctx.nodes[compId] = s.companions
    ? {
        type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
        props: { isVisible: true, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
        displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
        parent: sec, hidden: false, nodes: [compField], linkedNodes: {},
      }
    : {
        type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
        props: { isVisible: false, fieldCount: 0, fieldSpacing: "medium" },
        displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
        parent: sec, hidden: false, nodes: [], linkedNodes: {},
      };
  if (s.companions) {
    ctx.nodes[compField] = fi(compId, { label: "firstName", requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" });
  }

  // Overview slot (hidden; overviewEnabled drives it — left off by default).
  ctx.nodes[ovId] = {
    type: { resolvedName: "OverviewGroup" }, isCanvas: true,
    props: { isVisible: false, parentNodeId: sec, stepSections, stepLabels },
    displayName: "summaryGroup", custom: { icon: "IconListDetails" },
    parent: sec, hidden: false, nodes: [ovTitle, ovText], linkedNodes: {},
  };
  ctx.nodes[ovTitle] = tc(ovId, { isVisible: true, text: prepLang(undefined, ctx), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true });
  ctx.nodes[ovText] = tc(ovId, { isVisible: true, text: prepLang(undefined, ctx), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false });

  // Consent checkboxes.
  // An empty/missing `field` is a pure acknowledgement: the row gets NO
  // inputFieldVariable key (the reader hands it back as `field: ""`). A
  // REQUIRED row bound to emailOptIn keeps `required` and loses the binding
  // (GDPR Art. 7(4)) — dropped, not rejected, so AI edits of older pages that
  // still carry the coupling compile instead of failing.
  const cbList = (s.checkboxes ?? []).map((cb) => {
    const field = stripTags(cb.field || "");
    const required = cb.required ?? false;
    const row: { text: string | Record<string, string>; required: boolean; inputFieldVariable?: string } = {
      text: prepLang(cb.label, ctx),
      required,
    };
    if (!field) return row;
    if (!/^[A-Za-z][\w]*$/.test(field)) fail("invalidStructure", `checkbox field "${cb.field}"`);
    if (!(required && field === EMAIL_OPT_IN_FIELD)) {
      row.inputFieldVariable = field;
    } else {
      // Audit 7.12 M8: say so. Older pages carried this coupling, so a
      // routine AI edit (a hero text) used to unbind the page's only opt-in
      // without a word, and new registrants stopped recording emailOptIn.
      // No optional row is added on the model's behalf — the organiser decides.
      ctx.warnings.push(optInDroppedWarning(primStr(cb.label, ctx)));
    }
    return row;
  });
  ctx.nodes[cbId] = {
    type: { resolvedName: "Checkboxes" }, isCanvas: false,
    props: { isVisible: cbList.length > 0, checkboxes: cbList, displayName: "checkboxes" },
    displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
    parent: sec, hidden: false, nodes: [], linkedNodes: {},
  };

  // Submit / step-nav buttons.
  ctx.nodes[btnId] = btn(sec, { linkType: "internal", linkUrl: "", text: prepLang(s.submitText || ctx.defaults.submitText, ctx), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" });
  ctx.nodes[backId] = btn(sec, { linkType: "internal", type: "secondary", linkUrl: "", text: multiStep ? "Back" : "", navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" });
  ctx.nodes[nextId] = btn(sec, { linkType: "internal", linkUrl: "", text: multiStep ? "Next" : "", navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" });
  return sec;
}

// ── Page + website compile ───────────────────────────────────────────────────

function compilePage(
  page: WebsiteDslPage,
  dsl: WebsiteDsl,
  fieldNames: Set<string>,
  defaults: WebsiteCompileDefaults,
  warnings: string[],
): CompiledPage {
  if (!page.slug || !SLUG.test(page.slug)) fail("invalidSlug", page.slug);
  if (!Array.isArray(page.sections) || page.sections.length === 0) fail("noSections", page.slug);
  if (page.sections.length > MAX_SECTIONS_PER_PAGE) fail("tooManySections", `max ${MAX_SECTIONS_PER_PAGE}`);

  let n = 0;
  // Theme palette for the `background` token (preset/base-defaulted strict hex).
  const themeCfg = buildThemeConfig(dsl.theme);
  const ctx: Ctx = {
    nodes: {},
    mint: () => `w${(n++).toString(36)}`,
    body: "",
    primary: normalizeToFullLocale(dsl.primaryLanguage ?? "de-DE") ?? "de-DE",
    languages: new Set(
      [dsl.primaryLanguage ?? "de-DE", ...(dsl.languages ?? [])]
        .map((l) => normalizeToFullLocale(l))
        .filter((l): l is string => !!l),
    ),
    palette: {
      primary: themeCfg.primaryColor ?? "#000000",
      secondary: themeCfg.secondaryColor ?? "#000000",
      background: themeCfg.backgroundColor ?? "#ffffff",
    },
    display: dsl.theme?.displayScale === "display",
    defaults,
    warnings,
  };

  // Scaffold first so the Body id is known to section builders as parent.
  const ids = { ab: ctx.mint(), abs: ctx.mint(), body: ctx.mint(), ft: ctx.mint(), fts: ctx.mint() };
  ctx.body = ids.body;

  const bodyNodes: string[] = [];
  for (const section of page.sections) {
    switch (section.kind) {
      case "hero": bodyNodes.push(buildHero(section, ctx)); break;
      case "text": bodyNodes.push(buildText(section, ctx)); break;
      case "image": bodyNodes.push(buildImage(section, ctx)); break;
      case "register": bodyNodes.push(buildRegister(section, ctx, fieldNames)); break;
      case "agenda": bodyNodes.push(buildAgenda(section, ctx)); break;
      case "speaker": bodyNodes.push(buildSpeaker(section, ctx)); break;
      case "testimonials": bodyNodes.push(buildTestimonials(section, ctx)); break;
      case "numbers": bodyNodes.push(buildNumbers(section, ctx)); break;
      case "faq": bodyNodes.push(buildFaq(section, ctx)); break;
      case "gallery": bodyNodes.push(buildGallery(section, ctx)); break;
      case "countdown": bodyNodes.push(buildCountdown(section, ctx)); break;
      case "map": bodyNodes.push(buildMap(section, ctx)); break;
      case "login": bodyNodes.push(buildLogin(section, ctx)); break;
      case "datainput": bodyNodes.push(buildDataInput(section, ctx, fieldNames)); break;
      default: fail("unknownSectionKind", (section as { kind?: string }).kind ?? "(missing)");
    }
  }

  const tree = {
    ...scaffold(ids, bodyNodes, false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-md" }),
    ...ctx.nodes,
  } as Record<string, N>;
  return { slug: page.slug, pageName: stripTags(page.pageName || "Home") || "Home", tree };
}

/**
 * Compile a full website DSL into per-page Craft trees. Returns a structured
 * error (no throw) so the client tool can surface it back to the model for
 * self-correction.
 */
export function buildWebsiteCraftJsFromBlocks(
  dsl: WebsiteDsl,
  fieldNames: Iterable<string>,
  /** Required, so tsc names every lane that has to supply localized labels. */
  defaults: WebsiteCompileDefaults,
): WebsiteCompileOk | WebsiteCompileError {
  try {
    if (!Array.isArray(dsl.pages) || dsl.pages.length === 0) {
      return { ok: false, errorCode: "noPages" };
    }
    if (dsl.pages.length > MAX_PAGES) {
      return { ok: false, errorCode: "tooManyPages", detail: `max ${MAX_PAGES}` };
    }
    // Exactly-one home page + unique slugs (else homeSlug "/" dangles / the
    // backend rejects dup slugs with an opaque validationError).
    const slugList = dsl.pages.map((p) => p.slug);
    if (!slugList.includes("/")) {
      return { ok: false, errorCode: "invalidSlug", detail: "one page must be the home page with slug /" };
    }
    if (new Set(slugList).size !== slugList.length) {
      return { ok: false, errorCode: "invalidSlug", detail: "duplicate page slug" };
    }
    const names = new Set(fieldNames);
    const warnings: string[] = [];
    const pages = dsl.pages.map((p) => compilePage(p, dsl, names, defaults, warnings));

    // Backstop: structural self-check turns any future factory regression into
    // a soft error instead of a bricked editor.
    for (const p of pages) {
      const structural = validateWebsiteCraftStructure(JSON.stringify(p.tree));
      if (!structural.ok) {
        return { ok: false, errorCode: "invalidStructure", detail: `${p.slug}: ${structural.detail}` };
      }
    }
    return warnings.length > 0 ? { ok: true, pages, warnings: capWarnings(warnings) } : { ok: true, pages };
  } catch (e) {
    if (e instanceof CompileFail) return { ok: false, errorCode: e.code, detail: e.detail };
    // A malformed doc can still throw a raw TypeError (e.g. a null entry reached
    // before validation). Convert to a structured result so the tool handler
    // returns { ok:false } instead of crashing.
    return { ok: false, errorCode: "invalidStructure", detail: e instanceof Error ? e.message : String(e) };
  }
}
